import { useState } from 'react';
import { API_URL } from '@constants';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';

const ENDPOINT_URL = '/usr-alerts';

export interface DeleteAlertSuccessResponse {
    status: number;
    success: boolean;
    message: string;
}

/**
 * Requests endpoint to delete an alert by uuid.
 */
export const useDeleteAlert = () => {
    const { getHeaders, handleCommonErrors } = useApiConfig();
    const [loading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<number | null>(null);

    const request = async (uuid: string): Promise<DeleteAlertSuccessResponse | undefined> => {
        setLoading(true);
        setSuccess(false);
        setSuccessMessage(null);
        setErrorMessage(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}/${uuid}`, {
                method: 'DELETE',
                headers: getHeaders(),
            });

            const json = await response.json();

            if (!response.ok) {
                const error = new Error(json.message || json.error || 'Erro ao eliminar alerta') as ApiError;
                error.statusCode = response.status;
                setErrorMessage(json.message || json.error || 'Erro ao eliminar alerta');
                throw error;
            }

            const data: DeleteAlertSuccessResponse = json;
            setSuccess(true);
            setSuccessMessage(data.message);
            setErrorMessage(null);

            return data;
        } catch (err: any) {
            setSuccess(false);
            handleCommonErrors(err);
        } finally {
            setLoading(false);
            setLastResponseTime(Date.now());
        }
    };

    return {
        request,
        loading,
        success,
        successMessage,
        errorMessage,
        lastResponseTime,
    };
};
